/**
 * Sidebar (iter200)
 *
 * Drag-reorderable, badge-aware sidebar. Replaces the two hand-rolled
 * icon-column loops that used to live inline in `DashboardLayout.jsx`.
 *
 * Contract:
 *   - `items`: full ordered list of tabs [{ icon, label, path, badge?, testId? }].
 *     `badge` is optional; when a positive integer, a small red pill
 *     renders at the icon's top-right corner. `testId` is optional and
 *     overrides the label-derived slug — required for any item whose label
 *     is translated, so `data-testid`s stay stable across languages.
 *   - `activePath`: whichever tab should show as "current" (usually
 *     `location.pathname`).
 *   - `onNavigate(path)`: parent-supplied navigation callback.
 *   - `onOrderChange(newPaths)`: fires with the reordered array of
 *     paths after every successful drag. Parent persists it.
 *   - `renderBadgeSlot(item)`: optional render-prop for special badges
 *     (e.g. the Sentiment Alerts snooze popover replaces the plain
 *     numeric pill with a clickable Popover trigger). Called for every
 *     item; return `null` to fall back to the default pill.
 */
import React from 'react';
import {
  DndContext, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { OpenInNewTabButton, isModifiedClick } from '@/components/common/NewTabAffordance';

const SortableSidebarItem = ({ item, active, onNavigate, renderBadgeSlot }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.path });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const customBadge = renderBadgeSlot ? renderBadgeSlot(item) : null;
  const testIdSlug = item.testId || item.label.toLowerCase().replace(/\s+/g, '-');

  const Icon = item.icon;

  // iter248 — a pointerup that ends a drag still fires a click on the anchor,
  // which would navigate to whatever tab you just dragged. Remember that a
  // drag happened and swallow exactly that one click. The flag self-clears so
  // an abandoned drag can't eat a later legitimate click.
  const draggedRef = React.useRef(false);
  React.useEffect(() => {
    if (isDragging) {
      draggedRef.current = true;
      return undefined;
    }
    if (!draggedRef.current) return undefined;
    const t = setTimeout(() => { draggedRef.current = false; }, 250);
    return () => clearTimeout(t);
  }, [isDragging]);

  // Plain click navigates in-app; a modified click falls through to the
  // browser so Cmd/Ctrl+click opens a new tab like any other link.
  const handleClick = (e) => {
    if (draggedRef.current) {
      e.preventDefault();
      draggedRef.current = false;
      return;
    }
    if (isModifiedClick(e)) return;
    e.preventDefault();
    onNavigate(item.path);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
      data-testid={`sidebar-drag-${testIdSlug}`}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className={`h-10 w-10 text-white hover:bg-[#DC2626] rounded-lg touch-none ${
              active ? 'bg-[#DC2626]' : ''
            }`}
            data-testid={`sidebar-${testIdSlug}`}
            {...attributes}
            {...listeners}
          >
            {/* `draggable={false}` is required: a native anchor drag would
                hijack the pointer and break dnd-kit's sortable drag. */}
            <a href={item.path} onClick={handleClick} draggable={false}>
              <Icon className="h-5 w-5" />
            </a>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{item.label}</p>
        </TooltipContent>
      </Tooltip>

      {/* "Open in a new browser tab", revealed on hover at the icon's
          BOTTOM-right so it never collides with the badge slot above.
          iter249: the rail keeps this hover chip — the top nav's permanent,
          spaced-out icon looked cluttered next to a 40px icon-only button —
          but the right-click menu is gone from here, the chip covers it. */}
      <OpenInNewTabButton
        path={item.path}
        label={item.label}
        slug={`sidebar-${testIdSlug}`}
        className="absolute -bottom-1 -right-1 h-4 w-4 bg-[#0A1024] border border-[#1F2A4D] text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
      />

      {/* Badge slot — either a custom render (Sentiment Alerts snooze
          popover) or the default numeric pill. Both sit at the icon's
          top-right corner and swallow click events so tapping the badge
          NEVER navigates the sidebar item. */}
      {customBadge || (
        item.badge && item.badge > 0 ? (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-semibold flex items-center justify-center pointer-events-none"
            data-testid={`sidebar-badge-${testIdSlug}`}
          >
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        ) : null
      )}
    </div>
  );
};

const Sidebar = ({ items, activePath, onNavigate, onOrderChange, renderBadgeSlot }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require ~8px pointer movement before a drag begins so casual
      // clicks/taps on the icons still navigate.
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.path === active.id);
    const newIdx = items.findIndex((i) => i.path === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = [...items];
    const [moved] = next.splice(oldIdx, 1);
    next.splice(newIdx, 0, moved);
    onOrderChange?.(next.map((i) => i.path));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.path)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col items-center space-y-4 pb-4" data-testid="sidebar-sortable">
          {items.map((item) => (
            <SortableSidebarItem
              key={item.path}
              item={item}
              active={activePath === item.path}
              onNavigate={onNavigate}
              renderBadgeSlot={renderBadgeSlot}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default Sidebar;
